// This controller manages viewing context of a smaller image, cropped from a larger image

// image_context_view_controller.js
import ApplicationController from "./application_controller";
import { wordSearchNewTabUrl } from "../lib/word_search_new_tab_url";
import {calculatePolygonDimensions} from "../lib/array_utils";
import {drawPath} from "../service_workers/service-worker";
import {
  SearchResultHighlightPulseManager,
  buildPulsingSelectedHighlightStyle,
  resolvePulseCategoryStyle
} from "../lib/search_result_highlight_pulse";

export default class ImageContextViewController extends ApplicationController {
  static targets = [
    'imageModal',
    'draggableContext',
    'entryCoordinates',
    'leftNavigationBtn',
    'rightNavigationBtn',
    'contextWords',
    'contextWordBox',
    'contextCompoundWordBox',
    'ottomanWordBoxItem',
    'latinWordBoxItem',
    'combinedOttomanWordBoxItem',
    'combinedLatinWordBoxItem'
  ];

  initialize(){
    // Initialize empty arrays - will be populated in connect()
    this.entryCoordinatesData = [];
    this.contextWords = [];
    this.activeContextWord = null;
    this.searchResultPulse = new SearchResultHighlightPulseManager({
      isEnabled: () => this.isContextViewModal(),
      redrawTarget: (target) => this.redrawSearchResultPulseTarget(target)
    });
  }

  connect(){
    // Load entry coordinates data when controller connects to DOM - // This ensures data is loaded after AJAX content is inserted
    // Reduced delay for faster loading
    setTimeout(() => {
      this.loadEntryCoordinatesData();
    }, 0);
  }

  disconnect() {
    this.searchResultPulse.stop();
  }

  // Pulse animation is only for the location/context modal (not other image-context-view usages).
  isContextViewModal() {
    return this.hasImageModalTarget && this.imageModalTarget.classList.contains('context-modal');
  }

  loadEntryCoordinatesData() {
    // Reload entry coordinates data - call this when DOM is ready
    this.entryCoordinatesData = [];
    
    // First try to find multiple entryCoordinates targets (for combined words)
    const entryCoordinatesElements = this.element.querySelectorAll('[data-image-context-view-target="entryCoordinates"]');

    if (entryCoordinatesElements.length > 0) {
      // Multiple regions case (combined words)
      entryCoordinatesElements.forEach((target, index) => {
        try {
          const coordinates = JSON.parse(target.dataset.coordinates || '[]');
          const wordIds = JSON.parse(target.dataset.wordIds || '[]');
          
          this.entryCoordinatesData[index] = {
            coordinates: coordinates,
            wordIds:     wordIds,
            regionId:    target.dataset.regionId,
            canvasIndex: parseInt(target.dataset.canvasIndex) || 0
          };
        } catch (error) {
          console.error('Error parsing entry coordinates data:', error);
          this.entryCoordinatesData[index] = {
            coordinates: [],
            wordIds: [],
            regionId: null,
            canvasIndex: 0
          };
        }
      });
    } else {
      // Fallback: try to find firstEntryCoordinates (for single words/Column/Page context)
      const firstEntryCoordinatesElement = this.element.querySelector('[data-image-context-view-target="firstEntryCoordinates"]');
      if (firstEntryCoordinatesElement) {
        try {
          const coordinates = JSON.parse(firstEntryCoordinatesElement.dataset.coordinates || '[]');
          
          this.entryCoordinatesData[0] = {
            coordinates: [coordinates], // Wrap in array since we expect array of word coordinates
            wordIds: [firstEntryCoordinatesElement.dataset.wordId || 'unknown'],
            regionId: firstEntryCoordinatesElement.dataset.regionId || null,
            canvasIndex: 0
          };
        } catch (error) {
          console.error('Error parsing first entry coordinates data:', error);
          this.entryCoordinatesData[0] = {
            coordinates: [],
            wordIds: [],
            regionId: null,
            canvasIndex: 0
          };
        }
      }
    }

    // Load context words
    const contextWordsElement = this.element.querySelector('[data-image-context-view-target="contextWords"]');
    if (contextWordsElement) {
      try {
        this.contextWords = JSON.parse(contextWordsElement.dataset.contextWords || '[]');
      } catch (error) {
        console.error('Error parsing context words data:', error);
        this.contextWords = [];
      }
    }
    console.log('Loaded entry coordinates data:', this.entryCoordinatesData.length, 'regions');

    this.triggerImageDisplay() // Auto-trigger triggerImageDisplay after data is loaded
  }

  hotKeys(e){
    const eventObject = window.event ? event : e
    const left = 37
    const right = 39
    
    if (context_modal == undefined){
      return true;
    }

    if(eventObject.keyCode == left) {
      // Click left context navigate button
      this.leftNavigationBtnTarget.click();
    }

    if(eventObject.keyCode == right) {
      // Click right context navigate button
      this.rightNavigationBtnTarget.click();
    }
  }

  dragContext() {
    dragContextElement(this.draggableContextTarget);
  }

  triggerImageDisplay() {
    // Wait for modal to be fully shown and images to load
    setTimeout(() => {
      if (this.hasImageModalTarget) {
        this.imageModalTarget.querySelector('.context-first-image')?.click();
      }
    }, 50);
  }

  closeImageModal() {
    this.searchResultPulse.stop();

    let contextModalId;
    contextModalId = 'context-image-modal';

    const modalElement = document.getElementById(contextModalId);
    const modalInstance = bootstrap.Modal.getInstance(modalElement);
    modalInstance.hide();
    const backdrops = document.querySelectorAll('.modal-backdrop');
    backdrops.forEach((backdrop) => backdrop.remove());
  }

  // This method changes the image in the Full View section while typing (ottoman or latin), highlight and slice steps,
  // It helps in seeing the entry in the region, column and page. It also highlights the current entry in the image
  changeMainImage(e) {
    if (!this.hasImageModalTarget) {
      console.error('No imageModalTarget found');
      return;
    }
    
    if (!e || !e.target) {
      console.error('No event target found');
      return;
    }
   
    e.target.classList.add('active');

    this.searchResultPulse.stop();

    this.imageModalTarget.querySelectorAll('.modal-img').forEach(image => image.setAttribute('hidden', null))
    this.imageModalTarget.querySelectorAll('.btn-img').forEach(btn => btn.classList.remove('active'))
    this.imageModalTarget.querySelector('.modal-body').querySelectorAll('canvas').forEach(c => c.remove());

    const searchedWordId = this.imageModalTarget.dataset.searchedWordId;
    const isSearchOutputPage = window.location.pathname.includes('/search_output');
    let firstEntryOffsetY = 0; // Legacy/simple approach target (used for Slice)
    let firstWordYOnCanvas = 0; // Advanced calc storage (kept for non-Slice)

    // Get the image key from the target
    const imageKey = e.target.getAttribute('data-image-key');
    if (!imageKey) {
      console.error('No data-image-key found on target element');
      return;
    }

    // If data is empty, try to reload it
    if (!this.entryCoordinatesData || this.entryCoordinatesData.length === 0) {
      this.loadEntryCoordinatesData();
      // Wait a bit for data to load, then retry
      setTimeout(() => {
        this.changeMainImage(e);
      }, 150);
      return;
    }

    // If context words are not loaded, try to reload them
    if (!this.contextWords || this.contextWords.length === 0) {
      const contextWordsElement = this.element.querySelector('[data-image-context-view-target="contextWords"]');
      if (contextWordsElement) {
        try {
          this.contextWords = JSON.parse(contextWordsElement.dataset.contextWords || '[]');
        } catch (error) {
          console.error('Error parsing context words data:', error);
          this.contextWords = [];
        }
      }
    }
 
    const contextWordIds = Array.isArray(this.contextWords)
    ? new Set(this.contextWords.map(word => word.id).filter(id => id != null))
    : new Set();
    
    // Track completion of all highlight drawings to ensure auto-scroll happens after all images load
    const highlightPromises = [];
    // Collect offset values from each region to find the minimum, along with container info
    const regionScrollData = [];
    // Collect the actual matched search-result word position so auto-scroll lands on the
    // highlighted result (not just the topmost entry). Preferred over regionScrollData when present.
    const searchResultScrollData = [];
    
    this.entryCoordinatesData.forEach((entryData, index) => {
      // Find the canvas container - it's inside .image-canvas with data-canvas-index
      let canvasContainer = this.imageModalTarget.querySelector(`.image-canvas[data-canvas-index="${index}"]`);
      
      // Fallback: if no canvas with data-canvas-index, try to find any canvas container
      if (!canvasContainer) {
        canvasContainer = this.imageModalTarget.querySelector('.image-canvas');
        if (!canvasContainer) {
          // For Column/Page context, try to find the modal-with-canvas container
          canvasContainer = this.imageModalTarget.querySelector('.modal-with-canvas');
          if (!canvasContainer) {
            console.error(`Canvas container not found for index ${index}`);
            return;
          }
        }
      }

      // Find the active image for this canvas
      const activeImage = canvasContainer.querySelector('.' + imageKey);
      if (!activeImage) {
        console.error(`Active image not found for canvas ${index} with imageKey: ${imageKey}`);
        return;
      }

      activeImage.removeAttribute('hidden');

      // Create a promise that resolves when highlights are drawn for this image
      const highlightPromise = new Promise((resolve) => {
        // Helper function to ensure image has proper dimensions
        const ensureImageDimensions = (callback) => {
          // If image is loaded and has dimensions, proceed immediately
          if (activeImage.complete && activeImage.naturalWidth > 0 && activeImage.naturalHeight > 0) {
            // Wait a small amount to ensure layout is complete (clientWidth might be 0 initially)
            if (activeImage.clientWidth > 0 && activeImage.clientHeight > 0) {
              callback();
            } else {
              // Image loaded but not laid out yet - wait for next frame
              requestAnimationFrame(() => {
                if (activeImage.clientWidth > 0 && activeImage.clientHeight > 0) {
                  callback();
                } else {
                  // Fallback: use natural dimensions if client dimensions still not available
                  callback();
                }
              });
            }
          } else {
            // Image not loaded yet - wait for load event
            activeImage.addEventListener('load', () => {
              requestAnimationFrame(() => callback());
            }, { once: true });
          }
        };

        // Wait for image to load and have proper dimensions before drawing highlights
        ensureImageDimensions(() => {
          // Create canvas for this region
          const canvas = document.createElement('canvas');
          
          // Set canvas size to match image dimensions (prefer client dimensions for accurate scaling)
          const imageWidth = activeImage.clientWidth || activeImage.naturalWidth || 800;
          const imageHeight = activeImage.clientHeight || activeImage.naturalHeight || 600;
          canvas.width = imageWidth;
          canvas.height = imageHeight;
          
          // Style canvas to overlay image (CSS class handles positioning)
          // canvas.style.position = 'absolute';
          // canvas.style.top = '0';
          // canvas.style.left = '50%';
          // canvas.style.transform = 'translateX(-50%)';
          // Ensure canvas is clickable
          canvas.style.pointerEvents = 'auto';
          
          let offsetX = 0, offsetY = 0;
          if (activeImage.dataset.coordinates) {
            ({ minX: offsetX, minY: offsetY } = calculatePolygonDimensions(JSON.parse(activeImage.dataset.coordinates)));
          }

          // For Column/Page context, append to the image container, not canvas container
          const imageContainer = canvasContainer.querySelector('.image-canvas') || canvasContainer;
          
          // Ensure container is positioned relative for absolute positioning if needed
          if (window.getComputedStyle(imageContainer).position === 'static') {
            imageContainer.style.position = 'relative';
          }
          
          imageContainer.append(canvas);
          const context = canvas.getContext('2d');

          // Draw highlights (image is already loaded and has dimensions at this point)
          let regionMinY = null;
          
          // Use natural dimensions for coordinate calculations to ensure accuracy
          const scaleFactor = (activeImage.clientWidth || activeImage.naturalWidth) / activeImage.naturalWidth;
          
          // Draw highlights for each word in this region
          entryData.coordinates.forEach((wordPoints, wordIndex) => {
            // Ensure wordPoints is an array
            if (!Array.isArray(wordPoints)) {
              console.error(`Word points ${wordIndex} is not an array:`, wordPoints);
              return;
            }

            const points = wordPoints.map(point => ({
              x: (point.x - offsetX) * scaleFactor,
              y: (point.y - offsetY) * scaleFactor
            }));

            const wordId = entryData.wordIds[wordIndex];

            // IMPORTANT:
            // If contextWords exist and this legacy wordId is not among them,
            // then skip this polygon. (This was the extra/incorrect polygon.)
            if (contextWordIds.size > 0 && !contextWordIds.has(wordId)) {
              return;
            }

            const isExactSearchedWord = searchedWordId && wordId === searchedWordId;

            if (isSearchOutputPage) {
              // When smart context highlights exist, avoid legacy red underlay:
              // it bleeds through semi-transparent category colors on initial draw.
              const hasSmartContextHighlights = Array.isArray(this.contextWords) && this.contextWords.length > 0;

              if (!hasSmartContextHighlights) {
                drawPath({ context, points, closePath: true });
                context.fillStyle = 'rgba(255, 0, 0, 0.3)'; // Legacy fallback for pages without smart highlights
                context.fill();
                // Show border only for exact searched words
                if (isExactSearchedWord) {
                  context.strokeStyle = '#ff0000';
                  context.lineWidth = 2;
                  context.stroke();
                }
              }
            } else {
              // Original border-only highlighting for other pages
              drawPath({ context, points, closePath: true });
              context.stroke();
            }

            // Capture minimum Y for this region
            if (points.length > 0) {
              const localMinY = points.reduce((min, p) => Math.min(min, p.y), points[0].y);
              if (regionMinY === null || localMinY < regionMinY) {
                regionMinY = localMinY;
              }
            }
          });

          // Smart highlighting: highlight all context words with their category colors
          // Only draw smart highlights for words belonging to this region
          if (this.contextWords && this.contextWords.length > 0) {
            // Apply region filtering only for Slice (region), not for Column or Page
            let wordsToHighlight;
            if (imageKey === 'region') {
             // Only draw smart highlights for words belonging to this region (Slice view)
             const currentRegionId = entryData.regionId;
             wordsToHighlight = this.contextWords.filter(
               word => word.region_id == currentRegionId
             );
            } else {
            // For Column or Page view, draw all context words without filtering
            wordsToHighlight = this.contextWords;
            }
            
            // Only draw if there are words to highlight
            if (wordsToHighlight.length > 0) {
              this.drawSmartHighlights(context, wordsToHighlight, offsetX, offsetY, activeImage, scaleFactor);
              // Add click and hover handlers for non-entry, non-selected words
              this.addCanvasInteractivity(canvas, wordsToHighlight, offsetX, offsetY, activeImage, scaleFactor);

              // Capture the matched search-result word's canvas position so auto-scroll can
              // center on the highlighted result, even when it sits far down a column/page.
              const searchResultCenterY = this.getSearchResultCanvasCenterY(wordsToHighlight, offsetX, offsetY, scaleFactor);
              if (searchResultCenterY !== null) {
                searchResultScrollData.push({
                  canvasY: searchResultCenterY,
                  canvas: canvas,
                  index: index
                });
              }

              if (this.isContextViewModal() && wordsToHighlight.some(word => word.is_search_result)) {
                this.searchResultPulse.registerTarget({
                  canvas,
                  wordsToHighlight,
                  offsetX,
                  offsetY,
                  activeImage,
                  scaleFactor
                });
              }
            }
          }
          context.closePath();
          
          // Store the minimum Y for this region along with the canvas for scroll calculation
          if (regionMinY !== null) {
            regionScrollData.push({
              canvasY: regionMinY,
              canvas: canvas,
              index: index
            });
          }
          
          // Resolve promise after highlights are drawn
          resolve();
        });
      });
      
      highlightPromises.push(highlightPromise);
    });

    // Auto-scroll to the matched result - wait for all highlights to be drawn first
    Promise.all(highlightPromises).then(() => {
      this.searchResultPulse.startIfNeeded();

      // Prefer inner scroll container if present; fallback to modal-body
      const scrollableContainer = this.imageModalTarget.querySelector('.context-scroll-container') || this.imageModalTarget.querySelector('.modal-body');

      // Prefer scrolling to the actual matched search-result word; fall back to the topmost
      // entry word only when no search result is present on the page (e.g. non-search contexts).
      const scrollData = searchResultScrollData.length > 0 ? searchResultScrollData : regionScrollData;

      if (scrollableContainer && scrollData.length > 0) {
        // Pick the visually topmost target by its real on-screen position. Comparing raw
        // per-canvas Y would be wrong when candidates live on different canvases (e.g. a
        // combined word spanning regions in the Slice view), so we resolve to viewport Y first.
        const topmostTarget = scrollData.reduce((best, region) => {
          const viewportTop = this.canvasPointViewportTop(region.canvas, region.canvasY);
          return best === null || viewportTop < best.viewportTop ? { region, viewportTop } : best;
        }, null).region;

        this.scrollContainerToCanvasY(scrollableContainer, topmostTarget.canvas, topmostTarget.canvasY);
      } else {
        console.log('Auto-scroll skipped:', {
          hasContainer: !!scrollableContainer,
          hasRegions: scrollData.length > 0,
          scrollableContainer: scrollableContainer
        });
      }
    });

    e.stopPropagation();
    e.preventDefault();
  }

  // Returns the vertical center (in canvas pixels) of the matched search-result word(s) so
  // auto-scroll can land directly on the highlighted result. Combines all search-result
  // polygons (e.g. segments of a combined word) into a single bounding range.
  // Returns null when this region has no search result.
  getSearchResultCanvasCenterY(wordsToHighlight, offsetX, offsetY, scaleFactor) {
    let minY = null;
    let maxY = null;

    wordsToHighlight.forEach(word => {
      if (!word.is_search_result || !word.points || word.points.length === 0) return;

      word.points.forEach(point => {
        const y = (point.y - offsetY) * scaleFactor;
        if (minY === null || y < minY) minY = y;
        if (maxY === null || y > maxY) maxY = y;
      });
    });

    if (minY === null) return null;
    return (minY + maxY) / 2;
  }

  // Resolves a canvas-space Y (a point on the overlay canvas) to an absolute viewport Y,
  // so points on different canvases can be compared against each other.
  canvasPointViewportTop(canvas, canvasY) {
    if (canvas && typeof canvas.getBoundingClientRect === 'function') {
      return canvas.getBoundingClientRect().top + canvasY;
    }
    return canvasY;
  }

  // Smoothly scrolls the container so the given canvas-space Y (a point on the overlay canvas)
  // is centered in the viewport. Uses the canvas's own on-screen rect so the math is identical
  // for the Slice layout (canvas nested in an image container) and the Column/Page layout
  // (canvas absolutely positioned directly inside the scroll container).
  scrollContainerToCanvasY(scrollableContainer, canvas, canvasY) {
    if (!scrollableContainer) return;

    const viewportHeight = scrollableContainer.clientHeight || scrollableContainer.offsetHeight;
    let scrollTop;

    if (canvas && typeof canvas.getBoundingClientRect === 'function') {
      const canvasRect = canvas.getBoundingClientRect();
      const scrollContainerRect = scrollableContainer.getBoundingClientRect();
      // Canvas is drawn 1:1 with its displayed size, so canvasY maps directly to on-screen pixels.
      const targetWithinScroll = (canvasRect.top - scrollContainerRect.top) + scrollableContainer.scrollTop + canvasY;
      scrollTop = targetWithinScroll - (viewportHeight / 2);
    } else {
      scrollTop = canvasY - (viewportHeight / 2);
    }

    // Scroll immediately without any delay to prevent user seeing the movement
    scrollableContainer.scrollTop = Math.max(0, scrollTop);
  }

  // Paints one word polygon: optional outer glow (one extra translucent stroke ring), fill, border.
  // applyOuterGlow: only the search-result / focussed word in drawSmartHighlights; false for non-focussed hover overlay.
  // Even-odd clip (canvas rect + polygon) keeps glow strokes outside the fill so they do not muddy the interior.
  // highlightStyle.shadowBlur / shadowRgbCsv drive manual ring width and tint (not the canvas shadow properties).
  paintPolygonHighlight(context, points, highlightStyle, applyOuterGlow = false) {
    if (!highlightStyle) return;

    context.lineJoin = 'round';
    context.lineCap = 'round';

    // Style fields: shadowBlur / shadowRgbCsv are ring sizing + tint (not canvas shadow API).
    const glowRgbCsv = highlightStyle.shadowRgbCsv;
    const glowWidthScale = Number(highlightStyle.shadowBlur) || 0;
    const borderLineWidth = Math.max(0.5, highlightStyle.lineWidth || 1);

    if (applyOuterGlow && glowWidthScale > 0 && glowRgbCsv) {
      // Clip to "outside the fill" so wide glow strokes do not sit on top of the translucent interior.
      const { width: canvasWidth, height: canvasHeight } = context.canvas;
      const shouldClipGlowOutsideFill = Array.isArray(points) && points.length >= 3;
      context.save();
      if (shouldClipGlowOutsideFill) {
        context.beginPath();
        context.rect(0, 0, canvasWidth, canvasHeight);
        this.appendRoundedHighlightPathContours(context, points);
        context.clip('evenodd');
      }

      const outerGlowStroke = {
        extraLineWidth: glowWidthScale * 0.55,
        alpha: highlightStyle.glowAlpha ?? 0.1
      };
      this.drawRoundedHighlightPath(context, points);
      context.strokeStyle = `rgba(${glowRgbCsv}, ${outerGlowStroke.alpha})`;
      context.lineWidth = borderLineWidth + outerGlowStroke.extraLineWidth;
      context.stroke();
      context.restore();
    }

    this.drawRoundedHighlightPath(context, points);
    if (highlightStyle.fill) {
      context.fillStyle = highlightStyle.fill;
      context.fill();
    }
    context.strokeStyle = highlightStyle.stroke;
    context.lineWidth = borderLineWidth;
    context.stroke();
  }

  // True when this word is the polygon under the cursor, or another segment of the same combined_word_id (compound hover).
  isWordUnderPointerOrSameCompound(word, pointerWord) {
    if (!pointerWord || !word) {
      return false;
    }
    if (word.id === pointerWord.id) {
      return true;
    }
    if (word.combined_word_id && word.combined_word_id === pointerWord.combined_word_id) {
      return true;
    }
    return false;
  }

  redrawSearchResultPulseTarget(target) {
    const context = target.canvas.getContext('2d');
    context.clearRect(0, 0, target.canvas.width, target.canvas.height);
    this.drawSmartHighlights(
      context,
      target.wordsToHighlight,
      target.offsetX,
      target.offsetY,
      target.activeImage,
      target.scaleFactor,
      target.canvas._contextHoverWord || null
    );
  }

  // Used only for cropper context words with outside_focus_paragraph when the pointer is not over that word (or its compound).
  getNeutralIdleHighlightStyle() {
    return {
      fill: 'rgba(128, 128, 128, 0.16)',
      stroke: 'rgba(88, 88, 88, 0.5)',
      lineWidth: 1.5,
      shadowBlur: 0,
      shadowRgbCsv: null
    };
  }

  // Draw smart highlights for all context words with their category colors
  // AI generated method name, there is nothing smart in it
  // It draws paragraph's words
  // NOTE: contextWords should already be filtered by region_id before calling this method
  // outside_focus_paragraph (cropper modal only) marks words not in the current paragraph; gray until hover. Combined words need isWordUnderPointerOrSameCompound so all segments un-gray together.
  drawSmartHighlights(context, contextWords, offsetX, offsetY, activeImage, scaleFactor = null, hoveredWord = null) {
    // Draw pipeline:
    // 1) Resolve category (entry/related/subentry/other)
    // 2) Build style token (fill, border, glow)
    // 3) Paint each polygon consistently in base and hover redraws.
    const scale = scaleFactor !== null ? scaleFactor : (activeImage.clientWidth || activeImage.naturalWidth) / activeImage.naturalWidth;
    // Match reference "rounded box" feel for highlight borders.
    context.lineJoin = 'round';
    context.lineCap = 'round';
    const selectedWordIds = new Set(
      contextWords
        .filter(word => word.is_search_result)
        .map(word => word.id)
    );

    contextWords.forEach(word => {
      if (!word.points || word.points.length === 0) return;

      const points = word.points.map(point => ({
        x: (point.x - offsetX) * scale,
        y: (point.y - offsetY) * scale
      }));

      const highlightCategory = this.resolveHighlightCategory(word);
      const isSelectedWord = word.is_search_result || selectedWordIds.has(word.id);

      const isHoveredWord = this.isWordUnderPointerOrSameCompound(word, hoveredWord);
      // outside_focus_paragraph is set only in cropper ERB; slice context omits it. Gray pass is skipped when this word (or linked compound) is hovered.
      const useGrayIdleStyle = Boolean(word.outside_focus_paragraph) && !isHoveredWord;

      if (useGrayIdleStyle) {
        const neutralStyle = this.getNeutralIdleHighlightStyle();
        context.save();
        this.paintPolygonHighlight(context, points, neutralStyle, false);
        context.restore();
        return;
      }

      // Search hit (or any segment of the same combined hit) under the cursor — wider blur for the single outer ring.
      const isHoveredSelectedWord = Boolean(
        hoveredWord && isSelectedWord && (
          hoveredWord.id === word.id
          || (
            !!hoveredWord.combined_word_id
            && hoveredWord.combined_word_id === word.combined_word_id
          )
        )
      );
      const pulseAnimation = this.searchResultPulse.getAnimationState(isSelectedWord);
      const highlightStyle = this.getWordHighlightStyle(
        highlightCategory,
        isSelectedWord,
        pulseAnimation ? pulseAnimation.fillIntensity : 0.65,
        isHoveredSelectedWord,
        word.color,
        pulseAnimation
      );

      context.save();
      // isSelectedWord => one manual outer glow ring; region words never get applyOuterGlow.
      this.paintPolygonHighlight(context, points, highlightStyle, isSelectedWord);
      context.restore();
    });
  }

  // Add click and hover interactivity to canvas for all words (entry, non-entry, and selected/current words)
  addCanvasInteractivity(canvas, contextWords, offsetX, offsetY, activeImage, scaleFactor) {
    // Prevent adding multiple handlers to the same canvas
    if (canvas.dataset.interactivityAdded === 'true') {
      return;
    }
    canvas.dataset.interactivityAdded = 'true';
    canvas._contextHoverWord = null;
    
    // Ensure canvas is clickable and on top
    canvas.style.pointerEvents = 'auto';
    canvas.style.cursor = 'default';
    
    const scale = scaleFactor !== null ? scaleFactor : (activeImage.clientWidth || activeImage.naturalWidth) / activeImage.naturalWidth;
    
    // Store hover state
    let hoveredWord = null;
    let hoverHighlightPaths = null; // Changed to array to support multiple linked words
    // When the slice includes a focussed entry/related/subentry, run one initial highlight draw (static glow).
    const hasFocussedEntryCategory = contextWords.some(word => {
      const category = this.resolveHighlightCategory(word);
      return word.is_search_result && (category === 'entry' || category === 'related' || category === 'subentry');
    });

    // Helper to find all words that belong to the same combined word
    const findLinkedWords = (word) => {
      if (!word.combined_word_id) {
        // Not part of a combined word, return just this word
        return [{ word, points: word.points.map(point => ({
          x: (point.x - offsetX) * scale,
          y: (point.y - offsetY) * scale
        })) }];
      }
      
      // Find all words with the same combined_word_id
      const linkedWords = [];
      for (const contextWord of contextWords) {
        if (contextWord.combined_word_id === word.combined_word_id && contextWord.points && contextWord.points.length > 0) {
          const points = contextWord.points.map(point => ({
            x: (point.x - offsetX) * scale,
            y: (point.y - offsetY) * scale
          }));
          linkedWords.push({ word: contextWord, points });
        }
      }
      return linkedWords;
    };
    
    // Helper to find clickable word at coordinates
    const findClickableWordAt = (x, y) => {
      for (const word of contextWords) {
        if (!word.points || word.points.length === 0) continue;
        
        // Convert word coordinates to canvas coordinates
        const points = word.points.map(point => ({
          x: (point.x - offsetX) * scale,
          y: (point.y - offsetY) * scale
        }));
        
        // Check if point is inside this word's polygon
        if (this.isPointInPolygon(x, y, points)) {
          return { word, points };
        }
      }
      return null;
    };

    // Add click handler
    canvas.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const clicked = findClickableWordAt(x, y);
      
      if (clicked) {
        if (!clicked.word.ottoman_value) {
          console.warn('Clicked word has no ottoman_value:', clicked.word);
        }
        const isCombined = Boolean(clicked.word.combined_word_id && clicked.word.combined_word_ottoman_value);
        this.searchByScript(clicked.word, 'ottoman', isCombined);
      }
    });
    
    // Add hover handler for visual feedback
    canvas.addEventListener('mousemove', (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const hovered = findClickableWordAt(x, y);
      
      if (hovered && hovered.word.id !== hoveredWord?.id) {
        hoveredWord = hovered.word;
        canvas._contextHoverWord = hovered.word;
        // Get all linked words if this word is part of a combined word
        const linkedWordsData = findLinkedWords(hovered.word);
        hoverHighlightPaths = linkedWordsData.map(item => item.points);

        // Redraw canvas with hover effect for all linked words
        this.redrawCanvasWithHover(canvas, contextWords, offsetX, offsetY, activeImage, scaleFactor, hoveredWord, hoverHighlightPaths);
        canvas.style.cursor = 'pointer';
        this.showContextWordBoxForCanvasWord(
          hoveredWord,
          this.contextWordBoxAnchorFromCanvasWord(canvas, hovered.points),
          hoveredWord.combined_word_id ? this.contextWordBoxAnchorFromCanvasWord(canvas, hovered.points, 'bottom') : null
        );
      } else if (!hovered && hoveredWord) {
        hoveredWord = null;
        canvas._contextHoverWord = null;
        hoverHighlightPaths = null;

        // Redraw canvas without hover effect
        this.redrawCanvasWithHover(canvas, contextWords, offsetX, offsetY, activeImage, scaleFactor, null, null);
        canvas.style.cursor = 'default';
        // Delay hide so the user can still move the cursor into the word box.
        this.contextWordBoxHideTimer = setTimeout(() => this.hideContextWordBox(), 150);
      } else if (hovered) {
        canvas.style.cursor = 'pointer';
        hoveredWord = hovered.word;
        this.showContextWordBoxForCanvasWord(
          hovered.word,
          this.contextWordBoxAnchorFromCanvasWord(canvas, hovered.points),
          hovered.word.combined_word_id ? this.contextWordBoxAnchorFromCanvasWord(canvas, hovered.points, 'bottom') : null
        );
      } else {
        canvas.style.cursor = 'default';
      }
    });
    
    // Reset hover state when mouse leaves canvas
    canvas.addEventListener('mouseleave', (event) => {
      // Keep the box open when pointer moves from canvas into the box.
      if (this.hasContextWordBoxTarget && this.contextWordBoxTarget.contains(event.relatedTarget)) {
        return;
      }
      if (this.hasContextCompoundWordBoxTarget && this.contextCompoundWordBoxTarget.contains(event.relatedTarget)) {
        return;
      }

      if (hoveredWord) {
        hoveredWord = null;
        canvas._contextHoverWord = null;
        hoverHighlightPaths = null;
        this.redrawCanvasWithHover(canvas, contextWords, offsetX, offsetY, activeImage, scaleFactor, null, null);
        canvas.style.cursor = 'default';
      }
      this.hideContextWordBox();
    });

    if (hasFocussedEntryCategory) {
      this.redrawCanvasWithHover(canvas, contextWords, offsetX, offsetY, activeImage, scaleFactor, null, null);
    }
  }

  // Search individual or combined word (ottoman/latin) based on data-combined attribute.
  searchWord(event) {
    if (!this.activeContextWord) return;
    const isCombined = event.currentTarget.dataset.combined === 'true';
    this.searchByScript(this.activeContextWord, event.currentTarget.dataset.script, isCombined);
  }

  hideContextWordBox() {
    if (!this.hasContextWordBoxTarget) return;

    this.contextWordBoxHideTimer = setTimeout(() => {
      this.activeContextWord = null;
      this.contextWordBoxTarget.classList.remove('active');
      if (this.hasContextCompoundWordBoxTarget) {
        this.contextCompoundWordBoxTarget.classList.remove('active');
      }
    }, 150);
  }

  // Cancel any pending hide timer when the cursor enters the word box.
  keepContextWordBox() {
    clearTimeout(this.contextWordBoxHideTimer);
  }

  // Show the context word box for the canvas highlighted region.
  showContextWordBoxForCanvasWord(word, topAnchor, bottomAnchor = null) {
    if (!this.hasContextWordBoxTarget || !this.hasOttomanWordBoxItemTarget || !this.hasLatinWordBoxItemTarget) {
      return;
    }

    // Keep the hovered word payload so box clicks search the same word/compound.
    this.activeContextWord = word;
    // Read individual word values for the top word box.
    const ottomanValue = word.ottoman_value;
    const latinValue = word.latin_value;
    // Read combined word values for the bottom word box.
    const combinedOttomanValue = word.combined_word_ottoman_value;
    const combinedLatinValue = word.combined_word_latin_value;
    const hasOttomanValue = Boolean(ottomanValue);
    // Combined word box is shown only when compound Ottoman value is available.
    const hasCompoundValue = Boolean(word.combined_word_id && combinedOttomanValue);
    // Render top box text; hide missing script rows instead of placeholders.
    this.ottomanWordBoxItemTarget.textContent = ottomanValue;
    this.latinWordBoxItemTarget.textContent = latinValue;
    this.toggleScriptSectionAndDividerVisibility(this.contextWordBoxTarget, this.latinWordBoxItemTarget, Boolean(latinValue));
    this.contextWordBoxTarget.classList.toggle('context-word-box-single-value', hasOttomanValue && !latinValue);
    this.contextWordBoxTarget.classList.toggle('d-none', !hasOttomanValue);

    if (this.hasContextCompoundWordBoxTarget && this.hasCombinedOttomanWordBoxItemTarget && this.hasCombinedLatinWordBoxItemTarget) {
      // Render bottom (combined word) box text; hide missing script rows.
      this.combinedOttomanWordBoxItemTarget.textContent = combinedOttomanValue;
      this.combinedLatinWordBoxItemTarget.textContent = combinedLatinValue;
      this.toggleScriptSectionAndDividerVisibility(this.contextCompoundWordBoxTarget, this.combinedLatinWordBoxItemTarget, Boolean(combinedLatinValue));
      this.contextCompoundWordBoxTarget.classList.toggle('context-word-box-single-value', Boolean(combinedOttomanValue) && !combinedLatinValue);
      this.contextCompoundWordBoxTarget.classList.toggle('d-none', !combinedOttomanValue);
    }

    this.positionWordBoxes(topAnchor, bottomAnchor, hasOttomanValue, hasCompoundValue);
  }

  // Position and activate the individual and combined word boxes relative to the highlighted region.
  positionWordBoxes(topAnchor, bottomAnchor, hasOttomanValue, hasCompoundValue) {
    if (!topAnchor) return;

    // Individual word: above the top of the highlighted region
    if (hasOttomanValue) {
      this.contextWordBoxTarget.style.left = `${topAnchor.x}px`;
      this.contextWordBoxTarget.style.top = `${topAnchor.y - 8}px`;
      this.contextWordBoxTarget.style.transform = 'translate(-50%, -100%)';
      this.contextWordBoxTarget.classList.add('active');
    } else {
      this.contextWordBoxTarget.classList.remove('active');
    }

    // Combined word: separate box below the bottom of the same highlighted region
    if (this.hasContextCompoundWordBoxTarget && hasCompoundValue && bottomAnchor) {
      this.contextCompoundWordBoxTarget.style.left = `${bottomAnchor.x}px`;
      this.contextCompoundWordBoxTarget.style.top = `${bottomAnchor.y + 8}px`;
      this.contextCompoundWordBoxTarget.style.transform = 'translate(-50%, 0)';
      this.contextCompoundWordBoxTarget.classList.add('active');
    } else if (this.hasContextCompoundWordBoxTarget) {
      this.contextCompoundWordBoxTarget.classList.remove('active');
    }
  }

  // Toggle the visibility of the Script section and divider between the Ottoman and Latin word boxes.
  toggleScriptSectionAndDividerVisibility(wordBoxElement, scriptItemElement, hasScriptValue) {
    scriptItemElement.classList.toggle('d-none', !hasScriptValue);
    const dividerElement = wordBoxElement.querySelector('.context-word-box-divider');
    if (dividerElement) {
      dividerElement.classList.toggle('d-none', !hasScriptValue);
    }
  }
  // Helper method to get the anchor point for the context word box placed above or below the highlighted region.
  contextWordBoxAnchorFromCanvasWord(canvas, points, edge = 'top') {
    if (!canvas || !points || points.length === 0) {
      return null;
    }

    const canvasRect = canvas.getBoundingClientRect();
    const xValues = points.map((point) => point.x);
    const yValues = points.map((point) => point.y);

    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const y = edge === 'bottom' ? Math.max(...yValues) : Math.min(...yValues);

    return {
      x: canvasRect.left + ((minX + maxX) / 2),
      y: canvasRect.top + y
    };
  }

  // Resolve ottoman/latin value (individual or combined) from the word payload and open search.
  searchByScript(word, scriptType = 'ottoman', combined = false) {
    const searchValue = combined
      ? (scriptType === 'latin' ? word.combined_word_latin_value : word.combined_word_ottoman_value)
      : (scriptType === 'latin' ? word.latin_value : word.ottoman_value);
    this.openSearchOutput(searchValue);
  }

  // Open search output in a new tab with the provided word value.
  openSearchOutput(searchValue) {
    if (!searchValue) {
      return;
    }

    const searchUrl = wordSearchNewTabUrl(searchValue);
    if (!searchUrl) {
      return;
    }
    window.open(searchUrl, '_blank');
  }

  // Redraw canvas with hover effect
  redrawCanvasWithHover(canvas, contextWords, offsetX, offsetY, activeImage, scaleFactor, hoveredWord, hoverHighlightPaths) {
    const context = canvas.getContext('2d');

    // Clear canvas
    context.clearRect(0, 0, canvas.width, canvas.height);
    
    // Redraw all highlights
    this.drawSmartHighlights(context, contextWords, offsetX, offsetY, activeImage, scaleFactor, hoveredWord);

    // Focussed hit is already fully drawn above; skip overlay to avoid double fill on the search-result polygon.
    const isFocusedEntryHover = Boolean(
      hoveredWord && (
        hoveredWord.is_search_result
        || (!!hoveredWord.combined_word_id && contextWords.some((w) => (
          w.combined_word_id === hoveredWord.combined_word_id && w.is_search_result
        )))
      )
    );

    // Draw hover effect on top if word(s) are hovered
    // hoverHighlightPaths is an array of paths (one for each linked word in a combined word)
    if (hoveredWord && hoverHighlightPaths && hoverHighlightPaths.length > 0 && !isFocusedEntryHover) {
      const hoveredHighlightCategory = this.resolveHighlightCategory(hoveredWord);
      const isSelectedWord = Boolean(hoveredWord.is_search_result);
      const hoveredHighlightStyle = this.getWordHighlightStyle(
        hoveredHighlightCategory,
        isSelectedWord,
        0.75,
        true,
        hoveredWord.color
      );
      const shouldEnlargeHoveredPath = hoveredHighlightCategory === 'entry' || hoveredHighlightCategory === 'related' || hoveredHighlightCategory === 'subentry';

      hoverHighlightPaths.forEach(path => {
        if (!path || path.length === 0) return;

        const hoverPath = shouldEnlargeHoveredPath ? this.scalePolygonFromCenter(path, 1.018) : path;
        context.save();
        // Hover outline/fill only — no outer glow for non-focussed words.
        this.paintPolygonHighlight(context, hoverPath, hoveredHighlightStyle, false);
        context.restore();
      });
    }
  }

  resolveHighlightCategory(word) {
    // Normalize category labels coming from different sources (titles/translations).
    const normalizedCategoryLabel = (word?.category || '').toString().trim().toLowerCase();
    if (normalizedCategoryLabel === 'entry') return 'entry';
    if (normalizedCategoryLabel === 'subentry' || normalizedCategoryLabel === 'sub entry' || normalizedCategoryLabel.includes('subentry')) return 'subentry';
    if (normalizedCategoryLabel === 'other') return 'related';
    if (normalizedCategoryLabel === 'related' || normalizedCategoryLabel === 'related entry' || normalizedCategoryLabel.includes('related')) return 'related';
    return 'custom';
  }

  getWordHighlightStyle(highlightCategory, isSelectedWord = false, highlightAnimationIntensity = 0, isHovered = false, dictionaryCategoryColor = null, pulseAnimation = null) {
    // Palettes: passive -> muted; active selected -> match Popup_Entry / Popup_Subentry (entry fill = --bg-entry-hov).
    // Entry active fill matches Popup_Entry.html --bg-entry-hov: rgba(5, 150, 105, 0.25)
    // (same RGB as --col-entry; fill stays fixed; ring thickness from shadowBlur, not canvas shadow.)
    const stylePalette = {
      entry: { stroke: '#059669', passiveFill: 0.05, passiveStroke: 0.66, activeFillMin: 0.25, activeFillMax: 0.25 },
      related: { stroke: '#2563eb', passiveFill: 0.12, passiveStroke: 0.66, activeFillMin: 0.18, activeFillMax: 0.27 },
      subentry: { stroke: '#f59e0b', passiveFill: 0.05, passiveStroke: 0.66, activeFillMin: 0.16, activeFillMax: 0.26 }
    };

    const categoryRgbMap = {
      entry: '5, 150, 105',
      related: '37, 99, 235',
      subentry: '245, 158, 11'
    };

    const parsedDictionaryColor = this.parseColorToRgb(dictionaryCategoryColor);
    const usesCustomDictionaryColor = highlightCategory === 'custom' && parsedDictionaryColor;

    const resolvedCategoryKey = stylePalette[highlightCategory] ? highlightCategory : 'related';
    const categoryStyleConfig = usesCustomDictionaryColor
      ? { stroke: parsedDictionaryColor.stroke, passiveFill: 0.2, passiveStroke: 0.64, activeFillMin: 0.26, activeFillMax: 0.38 }
      : stylePalette[resolvedCategoryKey];
    const categoryRgb = usesCustomDictionaryColor
      ? `${parsedDictionaryColor.r}, ${parsedDictionaryColor.g}, ${parsedDictionaryColor.b}`
      : categoryRgbMap[resolvedCategoryKey];

    if (isSelectedWord) {
      const { categoryStyleConfig, resolvedCategoryKey, categoryRgb } = resolvePulseCategoryStyle({
        highlightCategory,
        dictionaryCategoryColor,
        parseColorToRgb: (color) => this.parseColorToRgb(color)
      });

      if (pulseAnimation) {
        return buildPulsingSelectedHighlightStyle({
          categoryStyleConfig,
          resolvedCategoryKey,
          categoryRgb,
          fillIntensity: pulseAnimation.fillIntensity,
          glowIntensity: pulseAnimation.glowIntensity,
          isHovered
        });
      }

      const fillAlpha = categoryStyleConfig.activeFillMin
        + ((categoryStyleConfig.activeFillMax - categoryStyleConfig.activeFillMin) * highlightAnimationIntensity);
      const selectedBorderWidth = resolvedCategoryKey === 'entry'
        ? 2
        : (isHovered ? 2.25 : 2);
      const blurKey = stylePalette[resolvedCategoryKey] ? resolvedCategoryKey : 'related';
      const idleFocusedGlowBlur = { entry: 5, related: 4, subentry: 4 };
      const shadowBlurBase = { entry: 15, related: 14, subentry: 14 };
      const shadowBlurSpan = { entry: 21, related: 20, subentry: 20 };
      const shadowBlur = isHovered
        ? (shadowBlurBase[blurKey] + (highlightAnimationIntensity * shadowBlurSpan[blurKey]))
        : (idleFocusedGlowBlur[blurKey] || 4);

      return {
        fill: `rgba(${categoryRgb}, ${fillAlpha.toFixed(3)})`,
        stroke: categoryStyleConfig.stroke,
        lineWidth: selectedBorderWidth,
        shadowBlur,
        shadowRgbCsv: categoryRgb
      };
    }

    // Passive: keep fill nearly unchanged on hover. shadowBlur unused here (rings only for selected; overlay uses applyOuterGlow false).
    const passiveFillAlpha = isHovered
      ? Math.min(categoryStyleConfig.passiveFill + 0.03, 0.12)
      : categoryStyleConfig.passiveFill;
    const passiveStrokeAlpha = isHovered ? Math.min(categoryStyleConfig.passiveStroke + 0.12, 0.85) : categoryStyleConfig.passiveStroke;

    return {
      fill: `rgba(${categoryRgb}, ${passiveFillAlpha.toFixed(3)})`,
      stroke: `rgba(${categoryRgb}, ${passiveStrokeAlpha.toFixed(3)})`,
      lineWidth: isHovered ? 1.8 : 2,
      shadowBlur: 0,
      shadowRgbCsv: categoryRgb
    };
  }

  // Uniformly scales a polygon around its center so hover highlights can grow
  // slightly without shifting their visual anchor on the image.
  scalePolygonFromCenter(points, scaleFactor = 1.0) {
    if (!Array.isArray(points) || points.length === 0 || scaleFactor === 1.0) {
      return points;
    }

    const center = points.reduce((acc, point) => {
      acc.x += point.x;
      acc.y += point.y;
      return acc;
    }, { x: 0, y: 0 });

    const cx = center.x / points.length;
    const cy = center.y / points.length;

    return points.map(point => ({
      x: cx + ((point.x - cx) * scaleFactor),
      y: cy + ((point.y - cy) * scaleFactor)
    }));
  }

  // Draw a polygon with true rounded corners (canvas equivalent of border-radius).
  drawRoundedHighlightPath(context, points, cornerRadius = 8) {
    if (!Array.isArray(points) || points.length === 0) return;
    if (points.length < 3) {
      drawPath({ context, points, closePath: true });
      return;
    }

    context.beginPath();
    this.appendRoundedHighlightPathContours(context, points, cornerRadius);
  }

  // Appends a closed rounded polygon to the current path (no beginPath). Callers only use this with >=3 points
  // (even-odd clip in paintPolygonHighlight, drawRoundedHighlightPath after its own <3 handling).
  appendRoundedHighlightPathContours(context, points, cornerRadius = 8) {
    if (!Array.isArray(points) || points.length < 3) return;

    const totalPoints = points.length;
    const getPoint = (index) => points[(index + totalPoints) % totalPoints];
    const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

    for (let i = 0; i < totalPoints; i++) {
      const previousPoint = getPoint(i - 1);
      const currentPoint = getPoint(i);
      const nextPoint = getPoint(i + 1);

      const previousLength = distance(previousPoint, currentPoint);
      const nextLength = distance(currentPoint, nextPoint);

      if (previousLength === 0 || nextLength === 0) {
        if (i === 0) {
          context.moveTo(currentPoint.x, currentPoint.y);
        } else {
          context.lineTo(currentPoint.x, currentPoint.y);
        }
        continue;
      }

      const effectiveRadius = Math.min(cornerRadius, previousLength / 2, nextLength / 2);

      const entryPoint = {
        x: currentPoint.x + ((previousPoint.x - currentPoint.x) / previousLength) * effectiveRadius,
        y: currentPoint.y + ((previousPoint.y - currentPoint.y) / previousLength) * effectiveRadius
      };

      const exitPoint = {
        x: currentPoint.x + ((nextPoint.x - currentPoint.x) / nextLength) * effectiveRadius,
        y: currentPoint.y + ((nextPoint.y - currentPoint.y) / nextLength) * effectiveRadius
      };

      if (i === 0) {
        context.moveTo(entryPoint.x, entryPoint.y);
      } else {
        context.lineTo(entryPoint.x, entryPoint.y);
      }

      context.quadraticCurveTo(currentPoint.x, currentPoint.y, exitPoint.x, exitPoint.y);
    }

    context.closePath();
  }

  // Point-in-polygon algorithm (ray casting)
  isPointInPolygon(x, y, points) {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x, yi = points[i].y;
      const xj = points[j].x, yj = points[j].y;
      
      const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  // DRY
  // Helper method to convert hex color to RGB
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  }

  parseColorToRgb(colorValue) {
    if (typeof colorValue !== 'string') return null;
    const normalizedColor = colorValue.trim();
    if (normalizedColor === '') return null;

    // Support #RGB / #RRGGBB formats coming from dictionary category settings.
    const shortHexMatch = normalizedColor.match(/^#([0-9A-Fa-f]{3})$/);
    if (shortHexMatch) {
      const expanded = shortHexMatch[1].split('').map(ch => ch + ch).join('');
      const rgb = this.hexToRgb(expanded);
      return { ...rgb, stroke: `#${expanded}` };
    }

    const longHexMatch = normalizedColor.match(/^#([0-9A-Fa-f]{6})$/);
    if (longHexMatch) {
      const rgb = this.hexToRgb(longHexMatch[1]);
      return { ...rgb, stroke: normalizedColor };
    }

    // Also accept rgb()/rgba() values if dictionary color is stored that way.
    const rgbMatch = normalizedColor.match(/^rgba?\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\)$/i);
    if (rgbMatch) {
      const r = Math.min(255, parseInt(rgbMatch[1], 10));
      const g = Math.min(255, parseInt(rgbMatch[2], 10));
      const b = Math.min(255, parseInt(rgbMatch[3], 10));
      return { r, g, b, stroke: `rgb(${r}, ${g}, ${b})` };
    }

    return null;
  }
}


// TODO: improve/modularize draggable functionality
// These methods help drag and drop of context modal 
const dragContextElement = (elmnt) => {
  var saveMouseUp = null;
  var saveMouseMove = null;

  if (elmnt) {
    elmnt.onmousedown = dragMouseDown;
  }

  function dragMouseDown(e) {
    e = e || window.event;

    e.preventDefault();
    // get the mouse cursor position at startup:
    saveMouseUp = document.onmouseup;
    saveMouseMove = document.onmousemove;

    document.onmouseup = closeDragElement;
    document.onmousemove = elementDrag;
  }

  function elementDrag(e) {
    e = e || window.event;
    e.preventDefault();

    // set the element's new position:
    elmnt.style.top = (e.clientY - 30) + "px";
    elmnt.style.left = (e.clientX - (elmnt.offsetWidth / 2)) + "px";
  }

  function closeDragElement() {
    // stop moving when mouse button is released:
    document.onmouseup = saveMouseUp;
    document.onmousemove = saveMouseMove;
  }
}
